import React, { useEffect, useRef, useState } from "react";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import BoardCard from "../BoardCard/BoardCard";
import { BoardButton, BoardIconButton } from "../../atoms/BoardDesignSystem";
import BoardCreateListForm from "../BoardCreateListForm/BoardCreateListForm";
import BoardDropdownPortal from "../BoardDropdownPortal/BoardDropdownPortal";
import "./BoardListColumn.scss";

const formatCollaboratorNames = (collaborators = []) => {
  const names = collaborators.map((person) => person.name).filter(Boolean);
  if (!names.length) return "only you";
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
};

const BoardListColumn = ({
  list,
  boardId,
  allCards = [],
  filteredCardIds = null,
  onCardClick,
  canCreate = false,
  canDrag = false,
  canDragList = false,
  canEditList = false,
  boardMembers = null,
  listDragHandleProps = null,
  onAddCard,
  onUpdateList,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [updatingList, setUpdatingList] = useState(false);
  const inputRef = useRef(null);
  const suppressClickRef = useRef(false);
  const editBtnRef = useRef(null);
  const editPanelRef = useRef(null);

  const hasActiveFilter = Array.isArray(filteredCardIds);
  const isPrivateList = list.visibility === "private";
  const hasAddCardRestriction = (list.addCardMemberIds || []).length > 0;
  /** Only show the restriction badge to people who cannot add cards. */
  const showAddCardRestrictionHint = hasAddCardRestriction && !canCreate;
  const listCards = allCards
    .filter((c) => c.listId === list.listId)
    .sort((a, b) => a.position - b.position);

  const visibleCards = hasActiveFilter
    ? listCards.filter((card) => filteredCardIds.includes(card.cardId))
    : listCards;

  const initialCollaboratorIds = (list.collaborators || []).map(
    (person) => Number(person.id || person.userId)
  );
  const initialAddCardMemberIds = (list.addCardMemberIds || []).map(Number);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  useEffect(() => {
    if (!editOpen) return undefined;

    const onDocClick = (e) => {
      if (
        !editBtnRef.current?.contains(e.target)
        && !editPanelRef.current?.contains(e.target)
      ) {
        setEditOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [editOpen]);

  const handleSubmitCard = async () => {
    const trimmed = title.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    try {
      await onAddCard?.(list.listId, trimmed, listCards.length);
      setTitle("");
      setIsAdding(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitCard();
    }
    if (e.key === "Escape") {
      setIsAdding(false);
      setTitle("");
    }
  };

  const handleCardClick = (card) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onCardClick?.(card);
  };

  const handleUpdateList = async (payload) => {
    if (updatingList) return;
    setUpdatingList(true);
    try {
      await onUpdateList?.(list.listId, payload);
      setEditOpen(false);
    } finally {
      setUpdatingList(false);
    }
  };

  return (
    <div className="pb-list">
      <div
        className={`pb-list__header ${canDragList ? "pb-list__header--draggable" : ""}`}
        {...(canDragList && listDragHandleProps ? listDragHandleProps : {})}
        title={canDragList ? "Drag to reorder list" : undefined}
      >
        <div className="pb-list__header-main">
          <h2 className="pb-list__title">{list.name}</h2>
          <div className="pb-list__header-actions">
            {canEditList && (
              <div
                className={`pb-list__edit-wrap ${editOpen ? "pb-list__edit-wrap--open" : ""}`}
                ref={editBtnRef}
              >
                <BoardIconButton
                  icon="✎"
                  label="Edit list settings"
                  variant="ghost"
                  className="pb-list__edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditOpen((open) => !open);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                {editOpen && (
                  <BoardDropdownPortal
                    open={editOpen}
                    anchorRef={editBtnRef}
                    menuRef={editPanelRef}
                    placement="bottom-end"
                    popoverWidth={340}
                    className="pb-list__edit-panel"
                  >
                    <h3 className="pb-list__edit-title">List settings</h3>
                    <BoardCreateListForm
                      submitting={updatingList}
                      initialName={list.name}
                      initialVisibility={list.visibility || "public"}
                      initialCollaboratorIds={initialCollaboratorIds}
                      initialAddCardMemberIds={initialAddCardMemberIds}
                      boardMembers={boardMembers}
                      submitLabel="Save"
                      autoFocusName={false}
                      onSubmit={handleUpdateList}
                      onCancel={() => setEditOpen(false)}
                    />
                  </BoardDropdownPortal>
                )}
              </div>
            )}
            <span className="pb-list__count">{visibleCards.length}</span>
          </div>
        </div>
        {(isPrivateList || showAddCardRestrictionHint) && (
          <div className="pb-list__sharing">
            {isPrivateList && (
              <>
                <span className="pb-list__badge">Private</span>
                {list.owner?.name && (
                  <span className="pb-list__sharing-text">
                    Created by <strong>{list.owner.name}</strong>
                  </span>
                )}
                <span className="pb-list__sharing-text pb-list__sharing-text--muted">
                  Shared with {formatCollaboratorNames(list.collaborators)}
                </span>
              </>
            )}
            {showAddCardRestrictionHint && (
              <>
                <span className="pb-list__badge pb-list__badge--add-card">Add card limited</span>
                <span className="pb-list__sharing-text pb-list__sharing-text--muted">
                  Add cards:{" "}
                  {(list.addCardMembers || []).length
                    ? formatCollaboratorNames(list.addCardMembers)
                    : `${(list.addCardMemberIds || []).length} member(s)`}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <Droppable droppableId={String(list.listId)} type="CARD">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`pb-list__cards-droppable ${snapshot.isDraggingOver ? "pb-list__cards-droppable--drag-over" : ""}`}
          >
            <div className="pb-list__cards-scroll">
              {visibleCards.map((card, index) => (
                <Draggable
                  key={String(card.cardId)}
                  draggableId={String(card.cardId)}
                  index={index}
                  isDragDisabled={!canDrag || hasActiveFilter}
                  disableInteractiveElementBlocking
                >
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      className={`pb-list__card-wrap ${dragSnapshot.isDragging ? "pb-list__card-wrap--dragging" : ""}`}
                      onMouseDown={() => {
                        suppressClickRef.current = false;
                      }}
                      onTouchStart={() => {
                        suppressClickRef.current = false;
                      }}
                      style={dragProvided.draggableProps.style}
                    >
                      <BoardCard
                        card={card}
                        boardId={boardId}
                        onClick={() => {
                          if (dragSnapshot.isDragging) {
                            suppressClickRef.current = true;
                            return;
                          }
                          handleCardClick(card);
                        }}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}

              {isAdding && (
                <div className="pb-list__add-form">
                  <textarea
                    ref={inputRef}
                    className="pb-list__add-input text-black"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter a title for this card..."
                    rows={3}
                    disabled={submitting}
                  />
                  <div className="pb-list__add-actions">
                    <BoardButton
                      variant="primary"
                      size="sm"
                      onClick={handleSubmitCard}
                      disabled={submitting || !title.trim()}
                    >
                      Add card
                    </BoardButton>
                    <BoardButton
                      variant="ghost-dark"
                      size="sm"
                      onClick={() => {
                        setIsAdding(false);
                        setTitle("");
                      }}
                    >
                      Cancel
                    </BoardButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Droppable>

      {canCreate && !isAdding && (
        <div className="pb-list__footer">
          <BoardButton
            variant="ghost-dark"
            block
            className="pb-list__add-card"
            onClick={() => setIsAdding(true)}
          >
            + Add a card
          </BoardButton>
        </div>
      )}
    </div>
  );
};

export default BoardListColumn;
